/**
 * communityService.js — Supabase data layer for the Community module.
 * All Sprint 2 community reads/writes route through here so screens
 * stay presentational. RLS enforces anonymity + ownership server-side.
 */
import { supabase } from './supabase';

/* Privacy-first MVP: display name is ALWAYS "Anonymous Member" in the UI,
 * regardless of the row's `anonymous` flag. The flag is still persisted
 * for future features (e.g. an opt-in "show my name" pass in Sprint 3). */
const AUTHOR_ANON = 'Anonymous Member';

/* Common English stopwords — used by similar-question detection. */
const STOPWORDS = new Set([
  'about','above','after','again','against','also','and','are','because','been',
  'before','being','below','between','both','but','can','could','does','doing',
  'done','down','during','each','for','from','further','had','has','have',
  'having','here','himself','herself','how','into','itself','just','more','most',
  'myself','not','only','other','ours','ourselves','out','over','over','same',
  'should','some','such','than','that','the','their','them','themselves','then',
  'there','these','they','this','those','through','under','until','very','was',
  'were','what','when','where','which','while','who','whom','why','will','with',
  'would','you','your','yours','yourself','yourselves'
]);

/* User-facing translation of common Supabase / PostgREST errors. */
export const humanizeError = (err) => {
  if (!err) return 'Something went wrong. Please try again.';
  const msg = typeof err === 'string' ? err : (err.message || '');
  if (/schema cache|does not exist|PGRST205/i.test(msg))
    return 'The community is warming up. Please try again in a moment.';
  if (/row-level security|permission denied/i.test(msg))
    return 'You need to be signed in to do that.';
  if (/duplicate key/i.test(msg))
    return 'Looks like that already exists.';
  if (/network|failed to fetch/i.test(msg))
    return 'Network hiccup — please check your connection and try again.';
  return 'Could not save right now. Please try again.';
};

/* Sanitize a user-supplied search term for embedding inside a PostgREST
 * `.or()` filter tree. Commas, parentheses, colons and ampersands are used
 * by PostgREST to parse the logic tree — leaving them in causes a
 * PGRST100 400. We also strip `%` and `_` so users can't accidentally
 * broaden the ilike pattern. Wrapped in `%…%` by the caller. */
const escapeSearchTerm = (raw) => {
  if (!raw) return '';
  // Collapse whitespace and drop PostgREST logic-tree separators.
  return raw
    .replace(/[%_,()&:*"'\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

/* Build a PostgREST `.or()` filter tree from a sanitized search string.
 * Tokens are ANDed within each token (across title/description/topic)
 * by requiring every token to appear in at least one column. Returns
 * an ARRAY of `.or(...)` argument strings so the caller can chain
 * one `.or()` per token. */
const searchOrClauses = (raw) => {
  const clean = escapeSearchTerm(raw);
  if (!clean) return [];
  const tokens = clean.split(' ').filter(t => t.length > 0);
  return tokens.map(t => {
    const s = `%${t}%`;
    return `title.ilike.${s},description.ilike.${s},topic.ilike.${s}`;
  });
};

/* Extract meaningful keywords from text: lowercase words ≥4 chars, non-stopword. */
const keywords = (text) => {
  if (!text) return [];
  const seen = new Set();
  const list = [];
  text.toLowerCase().replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .forEach(w => {
      if (w.length < 4) return;
      if (STOPWORDS.has(w)) return;
      if (seen.has(w)) return;
      seen.add(w);
      list.push(w);
    });
  return list;
};

/* ─── Feed ─────────────────────────────────────────────────── */
export const fetchQuestions = async ({ topic = null, search = '', limit = 20, before = null, beforeId = null } = {}) => {
  let q = supabase.from('community_questions')
    .select('id, user_id, anonymous, topic, title, description, attachment_url, created_at')
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })   // stable tie-breaker when created_at collides
    .limit(limit);
  if (topic)  q = q.eq('topic', topic);
  if (before) {
    // Composite cursor: rows strictly older, or equal-timestamp with a smaller id.
    if (beforeId) q = q.or(`created_at.lt.${before},and(created_at.eq.${before},id.lt.${beforeId})`);
    else q = q.lt('created_at', before);
  }
  if (search && search.trim()) {
    for (const clause of searchOrClauses(search)) {
      q = q.or(clause);
    }
  }
  const { data, error } = await q;
  if (error) { console.error('[community] fetchQuestions', error); throw error; }
  return (data || []).map(decorate);
};

/* ─── Sorted feed: Newest / Trending / Recommended ────────
 *
 * Newest      — created_at DESC (delegates to fetchQuestions).
 * Trending    — most experiences over the last 60 days,
 *               tie-break by recency.
 * Recommended — "hot" score = experiences / days_since_posted
 *               so a fresh question with 2 experiences beats an
 *               older one with 3. When personalisation lands later,
 *               this function alone changes — no UI change.
 *
 * For Trending / Recommended we pull a wider candidate window
 * (`window * 4`, capped at 100) then rerank in JS. When the corpus
 * grows past a few hundred questions per topic, this should move
 * into a Postgres materialised view. */
export const fetchSortedQuestions = async ({ sort = 'newest', topic = null, search = '', limit = 10, offset = 0 } = {}) => {
  if (sort === 'newest') {
    // Cursor pagination is only used inside topic pages; for the homepage
    // and All-Discussions views we use a simple offset window.
    return await fetchWindow({ topic, search, limit, offset, sort: 'newest' });
  }

  const candidateSize = Math.min(100, Math.max(limit * 5, 40));
  const candidates = await fetchWindow({ topic, search, limit: candidateSize, offset: 0, sort: 'newest' });
  if (candidates.length === 0) return [];

  const counts = await fetchExperienceCounts(candidates.map(c => c.id));
  const now = Date.now();

  const scored = candidates.map(c => {
    const totalCount = counts[c.id] || 0;
    // For Trending we bias toward RECENT engagement (last 60 days).
    // Since we only have per-question totals right now, we approximate
    // "recent engagement" by decaying older questions faster.
    const ageDays = Math.max(1, (now - new Date(c.created_at).getTime()) / 86_400_000);
    if (sort === 'trending') {
      // Older than 60 days: decay heavily so quiet older threads sink.
      const recencyWeight = ageDays <= 60 ? 1 : Math.max(0.15, 60 / ageDays);
      return { ...c, _score: totalCount * recencyWeight, _count: totalCount };
    }
    // 'recommended' — hot score.
    return { ...c, _score: totalCount / Math.pow(ageDays, 0.75) + totalCount * 0.01, _count: totalCount };
  });

  scored.sort((a, b) => (b._score - a._score) || (new Date(b.created_at) - new Date(a.created_at)));
  return scored.slice(offset, offset + limit);
};

/* Helper: newest-sorted window with offset (for homepage sections and
 * the All-Discussions screen). Not exported — callers use fetchSortedQuestions. */
const fetchWindow = async ({ topic, search, limit, offset }) => {
  let q = supabase.from('community_questions')
    .select('id, user_id, anonymous, topic, title, description, attachment_url, created_at')
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .range(offset, offset + limit - 1);
  if (topic)  q = q.eq('topic', topic);
  if (search && search.trim()) {
    for (const clause of searchOrClauses(search)) {
      q = q.or(clause);
    }
  }
  const { data, error } = await q;
  if (error) { console.error('[community] fetchWindow', error); throw error; }
  return (data || []).map(decorate);
};

/* Topic-diverse "featured" feed for the homepage.
 *
 * Draws from the most-recent ~30 discussions, keeps at most one per topic
 * (the newest of that topic), then trims to `limit`. If fewer distinct
 * topics are available than `limit`, remaining slots are filled with the
 * newest un-picked candidates.
 *
 * A light per-session jitter shuffles the ranking within the top window so
 * repeat visits within a day rotate the highlighted mix — organic, not
 * deterministic. Session-scoped, not persisted. */
export const fetchTopicDiverseFeed = async ({ limit = 3, candidateWindow = 30 } = {}) => {
  const candidates = await fetchWindow({ topic: null, search: '', limit: candidateWindow, offset: 0 });
  if (candidates.length === 0) return [];

  // Session-scoped jitter — same value within a page load, changes on refresh.
  if (typeof window !== 'undefined' && !window.__communityFeedSeed) {
    window.__communityFeedSeed = Math.random();
  }
  const seed = (typeof window !== 'undefined' && window.__communityFeedSeed) || 0;
  // Deterministic seeded shuffle so the same seed always produces the same order.
  const shuffled = [...candidates].sort((a, b) => {
    const ha = hashCode(a.id + ':' + seed);
    const hb = hashCode(b.id + ':' + seed);
    return ha - hb;
  });

  // Pick top-by-recency within jittered window, then diversify.
  const jittered = shuffled.slice(0, Math.min(candidateWindow, candidates.length));
  jittered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const chosen = [];
  const seenTopics = new Set();
  // Pass 1 — one per topic, newest first.
  for (const c of jittered) {
    if (chosen.length >= limit) break;
    if (seenTopics.has(c.topic)) continue;
    chosen.push(c); seenTopics.add(c.topic);
  }
  // Pass 2 — fill any remaining slots (when fewer topics available).
  for (const c of jittered) {
    if (chosen.length >= limit) break;
    if (chosen.find(x => x.id === c.id)) continue;
    chosen.push(c);
  }
  return chosen;
};

// Tiny stable string hash → int for the seeded shuffle.
const hashCode = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return h;
};

/* ─── Ask ─────────────────────────────────────────────────── */
export const fetchQuestion = async (id) => {
  // Try with the Sprint-2-polish columns first. If the DB has not yet
  // been migrated with SUPABASE_COMMUNITY_UNDERSTANDING.sql, PostgREST
  // returns 42703 ("column ... does not exist") — fall back gracefully.
  let { data, error } = await supabase.from('community_questions')
    .select('id, user_id, anonymous, topic, title, description, attachment_url, understanding, understanding_generated_at, tags, related_refs, external_ref, created_at, status')
    .eq('id', id).maybeSingle();
  if (error && (error.code === '42703' || /does not exist/i.test(error.message || ''))) {
    console.warn('[community] understanding columns missing — falling back');
    ({ data, error } = await supabase.from('community_questions')
      .select('id, user_id, anonymous, topic, title, description, attachment_url, created_at, status')
      .eq('id', id).maybeSingle());
  }
  if (error) { console.error('[community] fetchQuestion', error); throw error; }
  return data ? decorate(data) : null;
};

export const insertQuestion = async ({ userId, topic, title, description, anonymous, attachmentPath = null }) => {
  const { data, error } = await supabase.from('community_questions')
    .insert({ user_id: userId, topic, title, description, anonymous, attachment_url: attachmentPath })
    .select('id').single();
  if (error) { console.error('[community] insertQuestion', error); return { error: error.message }; }
  return { id: data.id };
};

/* Optional: upload attachment to Supabase Storage. Path is
 * `${userId}/${questionId or uuid}.${ext}`. Returns the storage path
 * (not URL) — we resolve to a signed URL only on read. */
export const uploadAttachment = async (userId, file) => {
  if (!file || !userId) return null;
  const ext = (file.name.split('.').pop() || 'jpg').toLowerCase().replace(/[^a-z0-9]/g, '');
  const path = `${userId}/${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from('community-uploads').upload(path, file, { upsert: false });
  if (error) { console.error('[community] uploadAttachment', error); return null; }
  return path;
};

/* Resolve a stored path to a signed URL for display. */
export const signedAttachmentUrl = async (path) => {
  if (!path) return null;
  const { data } = await supabase.storage.from('community-uploads').createSignedUrl(path, 3600);
  return data?.signedUrl || null;
};

/* One-shot warning flag so we only console.warn the missing-migration case once
 * per session — otherwise every question detail open would log the same 400. */
let _understandingCacheWarned = false;

/* ─── Understanding (Theo-generated, cached in DB) ─────────
 * If the row already has a cached `understanding` payload, return it.
 * Otherwise call the backend, then persist so future viewers get the
 * same content. Owner-only UPDATE is enforced by RLS — if the current
 * viewer is not the owner, the cache write silently no-ops (RLS will
 * reject the update); the payload is still returned to the caller. */
export const fetchOrGenerateUnderstanding = async (question) => {
  if (!question) return null;
  if (question.understanding && typeof question.understanding === 'object') {
    return question.understanding;
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const res = await fetch(`${backendUrl}/api/theo/community-understanding`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      topic: question.topic,
      title: question.title,
      description: question.description || '',
    }),
  });
  if (!res.ok) {
    console.error('[community] understanding HTTP', res.status);
    return null;
  }
  const payload = await res.json();
  // Persist back — RLS restricts UPDATE to owner. Also ignore PostgREST 
  // schema-cache errors (PGRST204) if the column hasn't been migrated yet.
  supabase.from('community_questions')
    .update({ understanding: payload, understanding_generated_at: new Date().toISOString() })
    .eq('id', question.id)
    .then(({ error }) => {
      if (!error) return;
      // 42501 = RLS refusal (non-owner). PGRST204 / 42703 = column missing pre-migration.
      const missingColumn = error.code === 'PGRST204' || error.code === '42703' || /understanding/i.test(error.message || '');
      if (missingColumn && !_understandingCacheWarned) {
        _understandingCacheWarned = true;
        console.warn('[community] Understanding cache write failing — has SUPABASE_COMMUNITY_UNDERSTANDING.sql been applied? Every view will re-invoke the LLM until then.');
        return;
      }
      if (missingColumn || error.code === '42501') return;
      console.warn('[community] cache understanding', error);
    });
  return payload;
};

/* ─── Similar-question detection (title + description) ─────
 * Stricter MVP rule: require ≥2 meaningful keyword overlaps
 * across (title + description), ignoring stopwords. */
export const findSimilarQuestions = async ({ topic, title, description = '', limit = 3 }) => {
  if (!title || title.trim().length < 6) return [];
  const words = keywords(`${title} ${description}`).slice(0, 6);
  if (words.length < 2) return [];
  const orClauses = words.map(w => `title.ilike.%${w}%,description.ilike.%${w}%`).join(',');
  let q = supabase.from('community_questions')
    .select('id, topic, title, description, created_at')
    .eq('status', 'active')
    .or(orClauses)
    .order('created_at', { ascending: false })
    .limit(limit * 5);
  if (topic) q = q.eq('topic', topic);
  const { data, error } = await q;
  if (error) { console.error('[community] findSimilar', error); return []; }
  // Require ≥2 keyword hits across title+description combined
  const scored = (data || []).map(row => {
    const hay = `${row.title} ${row.description || ''}`.toLowerCase();
    const score = words.reduce((n, w) => hay.includes(w) ? n + 1 : n, 0);
    return { ...row, _score: score };
  }).filter(r => r._score >= 2).sort((a, b) => b._score - a._score).slice(0, limit);
  return scored;
};

/* Related questions on the detail page.
 * If the question has curated `related_refs` (seeded content), resolve those first.
 * Otherwise (or if curated resolves to <limit rows), fill the remainder with
 * on-the-fly title-similarity within the same topic. */
export const fetchRelatedQuestions = async ({ questionId, topic, title, related_refs = null, limit = 3 }) => {
  const collected = [];
  const seen = new Set([questionId]);

  // 1) Curated refs first.
  if (Array.isArray(related_refs) && related_refs.length > 0) {
    const { data: refRows, error: refErr } = await supabase.from('community_questions')
      .select('id, topic, title, external_ref, created_at')
      .in('external_ref', related_refs)
      .eq('status', 'active');
    if (refErr && refErr.code !== '42703') console.warn('[community] related_refs', refErr);
    // Preserve the ORDER of related_refs from the batch.
    const byRef = new Map((refRows || []).map(r => [r.external_ref, r]));
    for (const ref of related_refs) {
      const r = byRef.get(ref);
      if (r && !seen.has(r.id)) { collected.push(r); seen.add(r.id); }
      if (collected.length >= limit) return collected;
    }
  }

  // 2) Fill remainder by title-similarity within topic.
  const remain = limit - collected.length;
  if (remain <= 0) return collected;
  const words = keywords(title || '').slice(0, 4);
  let q = supabase.from('community_questions')
    .select('id, topic, title, created_at')
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(remain * 4);
  if (questionId) q = q.neq('id', questionId);
  if (topic) q = q.eq('topic', topic);
  const { data } = await q;
  const rows = (data || []).filter(r => !seen.has(r.id));
  const ranked = words.length === 0
    ? rows.slice(0, remain)
    : rows.map(r => {
        const hay = r.title.toLowerCase();
        const score = words.reduce((n, w) => hay.includes(w) ? n + 1 : n, 0);
        return { ...r, _score: score };
      }).sort((a, b) => b._score - a._score).slice(0, remain);
  return [...collected, ...ranked];
};

/* ─── Experiences ─────────────────────────────────────────── */
export const fetchExperiences = async (questionId) => {
  const { data, error } = await supabase.from('community_experiences')
    .select('id, user_id, anonymous, experience, created_at, updated_at')
    .eq('question_id', questionId)
    .order('created_at', { ascending: false });
  if (error) { console.error('[community] fetchExperiences', error); return []; }
  return (data || []).map(e => ({
    ...e,
    author: AUTHOR_ANON,
    edited: !!(e.updated_at && e.updated_at !== e.created_at),
    when: relativeTime(e.created_at),
  }));
};

export const insertExperience = async ({ userId, questionId, anonymous, text }) => {
  const { data, error } = await supabase.from('community_experiences')
    .insert({ user_id: userId, question_id: questionId, anonymous, experience: text })
    .select('id, user_id, anonymous, experience, created_at, updated_at').single();
  if (error) { console.error('[community] insertExperience', error); return { error: error.message }; }
  return {
    row: { ...data, author: AUTHOR_ANON, edited: false, when: relativeTime(data.created_at) },
  };
};

/* Counts of experiences per question (used in feed cards). */
export const fetchExperienceCounts = async (questionIds = []) => {
  const out = {};
  if (!questionIds.length) return out;
  const { data, error } = await supabase.from('community_experiences')
    .select('question_id').in('question_id', questionIds);
  if (error) { console.error('[community] counts', error); return out; }
  (data || []).forEach(r => { out[r.question_id] = (out[r.question_id] || 0) + 1; });
  return out;
};

/* ─── Save / Unsave ─────────────────────────────────────── */
export const isSaved = async (userId, questionId) => {
  if (!userId) return false;
  const { data } = await supabase.from('community_saved_questions')
    .select('question_id').eq('user_id', userId).eq('question_id', questionId).maybeSingle();
  return Boolean(data);
};
export const toggleSave = async (userId, questionId, currentlySaved) => {
  if (!userId) return currentlySaved;
  if (currentlySaved) {
    await supabase.from('community_saved_questions').delete().eq('user_id', userId).eq('question_id', questionId);
    return false;
  }
  await supabase.from('community_saved_questions').insert({ user_id: userId, question_id: questionId });
  return true;
};

/* ─── Report ─────────────────────────────────────────────── */
export const reportQuestion = async ({ userId, questionId, reason }) => {
  const { error } = await supabase.from('community_reports')
    .insert({ reporter_id: userId, question_id: questionId, reason });
  return error ? { error: error.message } : { ok: true };
};

/* ─── Utilities ─────────────────────────────────────────── */
export const relativeTime = (iso) => {
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60)   return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs/60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs/3600)}h ago`;
  const d = Math.floor(secs / 86400);
  if (d < 7)  return `${d}d ago`;
  if (d < 30) return `${Math.floor(d/7)}w ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const decorate = (row) => ({
  ...row,
  author: AUTHOR_ANON,
  when:   relativeTime(row.created_at),
});
